import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useUserStore } from '~/stores/user/useUserStore';

export default (http: AxiosInstance) => {
    let isRefreshing = false;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    let failedQueue: { resolve: (value?: any) => void; reject: (reason?: any) => void }[] = [];

    function processQueue(error: AxiosError | null, token: string | null = null) {
        failedQueue.forEach((prom) => {
            if (error) prom.reject(error);
            else prom.resolve(token);
        });
        failedQueue = [];
    }

    http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const userStore = useUserStore();
        if (config.withCredentials && userStore.access) {
            config.headers['X-Access-Token'] = userStore.access;
        }
        return config;
    });

    http.interceptors.response.use(
        response => response,
        async (error: AxiosError) => {
            const userStore = useUserStore();
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
            if (error.response?.status === 401 && !originalRequest._retry) {
                if (!userStore.refresh) {
                    userStore.signOut();
                    return Promise.reject(error);
                }

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then((token) => {
                            if (originalRequest.headers) {
                                originalRequest.headers = {
                                    ...originalRequest.headers,
                                    'X-Access-Token': token as string
                                };
                            }
                            return http(originalRequest);
                        })
                        .catch(err => Promise.reject(err));
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const { data } = await http.post('/refresh', {
                        refreshToken: userStore.refresh
                    });

                    const { access, refresh } = data.data;
                    userStore.setAuth(access, refresh);

                    processQueue(null, access);

                    if (originalRequest.headers) {
                        originalRequest.headers['X-Access-Token'] = access;
                    }

                    return http(originalRequest);
                }
                catch (refreshError) {
                    processQueue(refreshError as AxiosError, null);
                    userStore.signOut();
                    return Promise.reject(refreshError);
                }
                finally {
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );
};
